import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Clock, CheckCircle, Trash2, UserPlus, Inbox, Filter, RefreshCw, MapPin } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const CLOTHING_COLORS = {
  "Shirt": "bg-blue-500/15 text-blue-300 border-blue-500/20",
  "Suit & Coat": "bg-purple-500/15 text-purple-300 border-purple-500/20",
  "Sherwani": "bg-amber-500/15 text-amber-300 border-amber-500/20",
  "Kurta": "bg-green-500/15 text-green-300 border-green-500/20",
  "Pathani": "bg-green-500/15 text-green-300 border-green-500/20",
  "Blazer": "bg-rose-500/15 text-rose-300 border-rose-500/20",
  "Waistcoat": "bg-rose-500/15 text-rose-300 border-rose-500/20",
  "Trousers & Pants": "bg-sky-500/15 text-sky-300 border-sky-500/20",
  "Other": "bg-gray-500/15 text-gray-300 border-gray-500/20",
};

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(isoStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function InquiriesPage() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | new | seen
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inquiries");
      setInquiries(r.data);
    } catch {
      toast.error("Failed to load inquiries");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const markSeen = async (id) => {
    try {
      await api.patch(`/inquiries/${id}/seen`);
      setInquiries(prev => prev.map(i => i.id === id ? { ...i, status: "seen" } : i));
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deleteInquiry = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/inquiries/${id}`);
      setInquiries(prev => prev.filter(i => i.id !== id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Failed to delete");
    }
    setDeletingId(null);
  };

  const convertToSlip = (inq) => {
    // Hand off the full inquiry (name, phone, address, items[]) via router state
    // so CreateSlipPage can (a) match an existing customer by exact phone and bind
    // the new slip to their old profile, or (b) seed a new customer + pre-fill
    // the requested clothing items. Keep name/phone in the URL as a fallback.
    navigate(
      `/slips/new?name=${encodeURIComponent(inq.name)}&phone=${encodeURIComponent(inq.phone)}`,
      { state: { inquiry: inq } }
    );
    markSeen(inq.id);
  };

  const filtered = inquiries.filter(i => {
    if (filter === "new") return i.status === "new";
    if (filter === "seen") return i.status === "seen";
    return true;
  });

  const newCount = inquiries.filter(i => i.status === "new").length;

  return (
    <div data-testid="inquiries-page" className="space-y-4 md:space-y-6 max-w-6xl">

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1 flex-wrap">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: "Georgia, serif" }}>
              Requested Customers
            </h1>
            {newCount > 0 && (
              <span className="bg-[#722F37] text-white text-xs font-bold px-2.5 py-1 rounded-full animate-pulse">
                {newCount} New
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Customers who filled the booking form on your website
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-[rgba(212,175,55,0.3)] transition-all"
        >
          <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 bg-[rgba(255,255,255,0.03)] border border-border rounded-sm p-1 w-full sm:w-fit overflow-x-auto">
        {[
          { key: "all", label: `All (${inquiries.length})` },
          { key: "new", label: `New (${newCount})` },
          { key: "seen", label: `Seen (${inquiries.length - newCount})` },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm rounded-sm transition-all font-medium whitespace-nowrap flex-1 sm:flex-none ${
              filter === tab.key
                ? "bg-[#722F37] text-white"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="bg-card border border-border rounded-sm p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-muted" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
              <div className="h-3 bg-muted rounded mb-2" />
              <div className="h-3 bg-muted rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-[rgba(114,47,55,0.15)] flex items-center justify-center mb-4">
            <Inbox className="w-7 h-7 text-muted-foreground" />
          </div>
          <p className="text-lg font-medium text-foreground mb-1">
            {filter === "all" ? "No inquiries yet" : `No ${filter} inquiries`}
          </p>
          <p className="text-sm text-muted-foreground max-w-xs">
            When customers fill the booking form on your website, their details will appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(inq => (
            <InquiryCard
              key={inq.id}
              inq={inq}
              onMarkSeen={markSeen}
              onDelete={deleteInquiry}
              onConvert={convertToSlip}
              deleting={deletingId === inq.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InquiryCard({ inq, onMarkSeen, onDelete, onConvert, deleting }) {
  const isNew = inq.status === "new";
  // Prefer the new items[] array; fall back to the legacy single clothing_type/message
  // so old inquiries created before multi-item support still render correctly.
  const items = Array.isArray(inq.items) && inq.items.length > 0
    ? inq.items
    : (inq.clothing_type ? [{ clothing_type: inq.clothing_type, message: inq.message || "" }] : []);

  return (
    <div
      data-testid={`inquiry-card-${inq.id}`}
      className={`relative bg-card border rounded-sm p-5 flex flex-col gap-4 transition-all hover:shadow-lg ${
        isNew
          ? "border-[rgba(212,175,55,0.35)] shadow-[0_0_0_1px_rgba(212,175,55,0.08)]"
          : "border-border"
      }`}
      onClick={() => isNew && onMarkSeen(inq.id)}
    >
      {/* New badge */}
      {isNew && (
        <span className="absolute top-3 right-3 text-[10px] font-bold text-[#D4AF37] bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.25)] px-2 py-0.5 rounded-full uppercase tracking-wider">
          New
        </span>
      )}

      {/* Customer info */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-[rgba(114,47,55,0.25)] flex items-center justify-center text-[#D4AF37] font-bold text-base flex-shrink-0 uppercase">
          {inq.name?.charAt(0) || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground text-sm truncate">{inq.name}</p>
          <a
            href={`tel:${inq.phone}`}
            onClick={e => e.stopPropagation()}
            className="text-xs text-muted-foreground hover:text-[#D4AF37] transition-colors flex items-center gap-1 mt-0.5"
          >
            <Phone className="w-3 h-3" /> {inq.phone}
          </a>
        </div>
      </div>

      {/* Clothing type badges (one per requested item) + relative time */}
      <div className="flex items-center gap-2 flex-wrap">
        {items.map((it, idx) => {
          const color = CLOTHING_COLORS[it.clothing_type] || CLOTHING_COLORS["Other"];
          return (
            <span
              key={idx}
              data-testid={`inquiry-item-badge-${inq.id}-${idx}`}
              className={`text-xs border px-2.5 py-1 rounded-full font-medium ${color}`}
            >
              {it.clothing_type}
            </span>
          );
        })}
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Clock className="w-3 h-3" /> {timeAgo(inq.created_at)}
        </span>
      </div>

      {/* Address (if provided) */}
      {inq.address && (
        <div
          className="flex items-start gap-2 bg-[rgba(255,255,255,0.02)] border border-border rounded-sm px-3 py-2"
          data-testid={`inquiry-address-${inq.id}`}
        >
          <MapPin className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground leading-relaxed">{inq.address}</p>
        </div>
      )}

      {/* Per-item requirement notes (shown only for items that have a message) */}
      {items.some(it => it.message) && (
        <div className="space-y-2">
          {items.map((it, idx) => it.message ? (
            <div
              key={idx}
              data-testid={`inquiry-item-message-${inq.id}-${idx}`}
              className="bg-[rgba(255,255,255,0.03)] border border-border rounded-sm p-3"
            >
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-widest font-semibold mb-1">
                {it.clothing_type}
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                "{it.message}"
              </p>
            </div>
          ) : null)}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2 flex-wrap mt-auto pt-1" onClick={e => e.stopPropagation()}>
        {/* WhatsApp */}
        <a
          href={`https://wa.me/${
              // If 10-digit Indian number, prepend 91. If already has country code, clean and use.
              (() => {
                const digits = inq.phone.replace(/[^0-9]/g, "");
                if (digits.length === 10) return "91" + digits;
                if (digits.startsWith("0") && digits.length === 11) return "91" + digits.slice(1);
                return digits;
              })()
            }`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs bg-[rgba(37,211,102,0.1)] hover:bg-[rgba(37,211,102,0.2)] text-[#25D366] border border-[rgba(37,211,102,0.2)] px-3 py-1.5 rounded-sm transition-all"
          title="WhatsApp"
        >
          <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          WhatsApp
        </a>

        {/* Call */}
        <a
          href={`tel:${inq.phone}`}
          className="flex items-center gap-1.5 text-xs bg-[rgba(212,175,55,0.08)] hover:bg-[rgba(212,175,55,0.15)] text-[#D4AF37] border border-[rgba(212,175,55,0.2)] px-3 py-1.5 rounded-sm transition-all"
          title="Call"
        >
          <Phone className="w-3.5 h-3.5" /> Call
        </a>

        {/* Create Slip */}
        <button
          onClick={() => onConvert(inq)}
          className="flex items-center gap-1.5 text-xs bg-[rgba(114,47,55,0.15)] hover:bg-[rgba(114,47,55,0.3)] text-[#f5a0aa] border border-[rgba(114,47,55,0.3)] px-3 py-1.5 rounded-sm transition-all ml-auto"
          title="Create Slip"
        >
          <UserPlus className="w-3.5 h-3.5" /> Create Slip
        </button>

        {/* Delete */}
        <button
          onClick={() => onDelete(inq.id)}
          disabled={deleting}
          className="flex items-center gap-1.5 text-xs bg-transparent hover:bg-red-500/10 text-muted-foreground hover:text-red-400 border border-transparent hover:border-red-500/20 px-2 py-1.5 rounded-sm transition-all"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Seen indicator */}
      {!isNew && (
        <div className="absolute bottom-3 right-3">
          <CheckCircle className="w-3.5 h-3.5 text-muted-foreground/40" />
        </div>
      )}
    </div>
  );
}
